/**
 * agent.ts — Shared wire contracts for Brok AI agent (Story P1a).
 *
 * Exported from @brokly/shared and consumed by:
 *   - apps/api: NestJS agent module (orchestrator, controller, tools)
 *   - apps/web: React chat hook + SSE event renderer (P1b)
 *   - apps/ai: Pydantic models mirror these by convention (not imported directly)
 *
 * Contents:
 *   - AgentMessageRole constant + type
 *   - AgentSseEventType + AgentSseEvent discriminated union (SSE wire protocol)
 *   - AgentToolDescriptor (model-facing tool list shape)
 *   - AgentChatRequestDto (POST /agent/chat body)
 *   - AgentMessageDto (persisted message replay shape)
 *   - PendingWorkSummary + EntitySummary (tool result payload types)
 *   - InvoiceConfirmFacts + helpers (P2b generative UI card)
 *
 * All types are plain interfaces/constants — no class-validator or class-transformer
 * so apps/web can import without NestJS deps.
 *
 * [Source: Story P1a spec §2]
 */

// ── Role constants ─────────────────────────────────────────────────────────────

/** Ordered tuple of valid agent message role values. */
export const AGENT_MESSAGE_ROLES = ['user', 'assistant', 'tool'] as const;

/** Union of valid agent message role string values. */
export type AgentMessageRole = (typeof AGENT_MESSAGE_ROLES)[number];

// ── SSE event types ────────────────────────────────────────────────────────────

/** All SSE event type discriminators emitted by POST /agent/chat. */
export type AgentSseEventType =
  | 'token'
  | 'tool_call'
  | 'tool_result'
  | 'message_complete'
  | 'awaiting_confirmation'
  | 'confirmation_resolved'
  | 'error';

/**
 * Discriminated union of all SSE events emitted by the Brok agent endpoint.
 *
 * Each variant is tagged by `type` (the discriminant).
 *
 * Protocol:
 *   1. The SSE stream begins.
 *   2. Zero or more `token` events stream the assistant's response tokens.
 *   3. When the model requests a tool call:
 *      a. A `tool_call` event is emitted with the requested tool + args.
 *      b. The tool executes (RBAC-gated, tenant-scoped).
 *      c. A `tool_result` event is emitted with the compact result.
 *   4. For write tools (P1b+): instead of step 3c, an `awaiting_confirmation`
 *      event is emitted. The stream pauses until the user confirms.
 *   5. A `message_complete` event closes the stream.
 *   6. On any error: an `error` event is emitted and the stream closes.
 */
export type AgentSseEvent =
  | AgentSseTokenEvent
  | AgentSseToolCallEvent
  | AgentSseToolResultEvent
  | AgentSseMessageCompleteEvent
  | AgentSseAwaitingConfirmationEvent
  | AgentSseConfirmationResolvedEvent
  | AgentSseErrorEvent;

/** A streamed token delta from the model's response. */
export interface AgentSseTokenEvent {
  type: 'token';
  /** The incremental text from the model (may be empty string). */
  delta: string;
}

/**
 * The model has requested a tool call.
 * Emitted BEFORE the tool executes (so the web client can show a loading state).
 */
export interface AgentSseToolCallEvent {
  type: 'tool_call';
  /** Tool name (kebab-case, e.g. 'get-pending-work'). */
  toolName: string;
  /** Correlation id (links this call to the matching tool_result event). */
  toolCallId: string;
  /** The validated args the model passed (may be empty object for no-arg tools). */
  args: Record<string, unknown>;
}

/**
 * The tool has executed and returned a result.
 * `result` is the compact summary — never a raw dump of all rows.
 */
export interface AgentSseToolResultEvent {
  type: 'tool_result';
  /** Correlation id (links back to the matching tool_call event). */
  toolCallId: string;
  /** Tool name. */
  toolName: string;
  /** Compact result summary (safe for the web client to display). */
  result: unknown;
}

/** The current assistant turn is complete. The stream ends after this event. */
export interface AgentSseMessageCompleteEvent {
  type: 'message_complete';
  /** The persisted agent_message.id for this assistant turn. */
  messageId: string;
}

/**
 * A WRITE tool has been requested (P1b+).
 * The orchestrator echoes the proposed action + key facts and waits for explicit confirmation.
 * No tool execution happens until the user sends a follow-up confirm message.
 *
 * Built in P1a so write tools can be added in P1b without rework.
 * No real write tools ship in P1a — all P1a tools are readonly.
 */
export interface AgentSseAwaitingConfirmationEvent {
  type: 'awaiting_confirmation';
  /** Tool name. */
  toolName: string;
  /** Correlation id (the pending tool call id). */
  toolCallId: string;
  /** Human-readable one-line summary of the proposed action. */
  summary: string;
  /** Key facts the user should verify (non-PII machine keys, e.g. { invoiceId, amount }). */
  facts: Record<string, unknown>;
}

/**
 * A pending write action has been RESOLVED (confirm / reject / rueckfrage / failed).
 *
 * Emitted by resolvePendingDecision on EVERY resolution branch, on BOTH the button path
 * (POST /agent/confirm) and the typed-NL path (streamChat NL-confirm branch). The web
 * client uses it to reconcile the matching `awaiting_confirmation` card's live buttons to
 * a final state, so a card can NEVER keep live buttons after the server resolved it, and a
 * failed action never stays shown as confirmed.
 *
 * `decision` is the RESOLVED state (past-tense), not the AgentDecision request enum:
 *   'approved'   : the tool executed successfully.
 *   'rejected'   : the user rejected the action (no execution).
 *   'rueckfrage' : the user asked a follow-up (no execution).
 *   'failed'     : the tool dispatch threw (execution attempted, but errored); the card
 *                  must leave the "confirmed" visual (map to a rejected/failed state).
 */
export interface AgentSseConfirmationResolvedEvent {
  type: 'confirmation_resolved';
  /** Correlation id: the pending tool call id from the awaiting_confirmation event. */
  toolCallId: string;
  /** The resolved (past-tense) state the card should reflect. */
  decision: 'approved' | 'rejected' | 'rueckfrage' | 'failed';
}

/** A recoverable or fatal error occurred during streaming. */
export interface AgentSseErrorEvent {
  type: 'error';
  /** Machine-readable error code (e.g. 'unknown_tool', 'invalid_args', 'model_error'). */
  code: string;
  /** Human-readable error message. */
  message: string;
}

// ── Tool descriptor ────────────────────────────────────────────────────────────

/**
 * Model-facing tool descriptor — what the model "sees" in the tools list.
 *
 * Constructed from the AgentTool registry by filtering to keys the user holds.
 * `requiredPermissionKey` is included here so the web client can display which
 * tools are available (no logic in web — just display).
 *
 * `readonly` is forwarded from `AgentTool.readonly` so the orchestrator can
 * branch on the confirm-before-write protocol without a separate ToolRuntime
 * lookup. This is the canonical write-gate signal: `readonly === false` triggers
 * `awaiting_confirmation`; `readonly === true` dispatches immediately.
 */
export interface AgentToolDescriptor {
  /** Kebab-case tool name (e.g. 'get-pending-work'). */
  name: string;
  /** One-line description sent to the model. */
  description: string;
  /** The permission key required to use this tool. */
  requiredPermissionKey: string;
  /**
   * Whether this tool is read-only.
   * `true`  → dispatch immediately (no confirmation needed).
   * `false` → confirm-before-write protocol: emit `awaiting_confirmation`,
   *           do NOT execute until the user sends an explicit confirm.
   */
  readonly: boolean;
}

// ── Request/response shapes ────────────────────────────────────────────────────

/**
 * POST /agent/chat request body.
 * The authenticated user + tenant come from the JWT — never from the body.
 */
export interface AgentChatRequestDto {
  /** The user's message text (or voice transcript in P2+). */
  message: string;
  /**
   * Optional id of a previously uploaded chat attachment (Feature B).
   * Returned by POST /agent/attachment. The bytes are resolved server-side from
   * the tenant/user-scoped object store — the tenant is taken from the JWT, never
   * from this id, so a foreign id resolves to nothing (tenant-isolated).
   */
  attachmentId?: string;
  /**
   * Optional original filename of the attached document (Feature B).
   * Display metadata only (the upload path persists no filename server-side), threaded
   * from the composer so the persisted user message can carry the "angehängt: <name>"
   * chip and it survives a reload. NEVER used to resolve bytes or scope the tenant.
   */
  attachmentName?: string;
}

/**
 * POST /agent/attachment response body (Feature B — extract-to-text MVP).
 *
 * The upload stores the raw bytes in the tenant/user-scoped object store and
 * returns an opaque id. The id is later passed on POST /agent/chat via
 * `attachmentId`; the orchestrator extracts the text layer and injects it as
 * context before the user's message.
 */
export interface AgentAttachmentResponseDto {
  /** Opaque id of the stored attachment — pass as AgentChatRequestDto.attachmentId. */
  attachmentId: string;
}

/**
 * GET /agent/conversation response item.
 * One row per persisted agent_message (ordered by seq ASC).
 */
export interface AgentMessageDto {
  /** agent_message.id */
  id: string;
  /** agent_message.role: 'user' | 'assistant' | 'tool' */
  role: AgentMessageRole;
  /** agent_message.content (null for pure tool-call turns) */
  content: string | null;
  /** agent_message.tool_name (non-null on tool rows + assistant tool-call rows) */
  toolName: string | null;
  /** agent_message.tool_call_id */
  toolCallId: string | null;
  /** agent_message.tool_result (null for non-tool rows) */
  toolResult: Record<string, unknown> | null;
  /** agent_message.seq */
  seq: number;
  /** agent_message.created_at */
  createdAt: Date | string;
  /**
   * Feature B: the document attached to this (user) turn, if any.
   * `filename` (agent_message.attachment_filename) drives the transcript attachment chip
   * on reload; `attachmentId` (agent_message.attachment_id) is the opaque per-turn id.
   * Absent on every message without an attachment (the overwhelming majority).
   */
  attachment?: { filename: string; attachmentId?: string };
}

// ── Confirm request (POST /agent/confirm) ──────────────────────────────────────

/**
 * Decision type for the confirm endpoint.
 * 'approve'     → execute the pending write tool.
 * 'reject'      → cancel; note required (≥10 chars).
 * 'rueckfrage'  → ask a follow-up question; note required (≥10 chars).
 */
export type AgentDecision = 'approve' | 'reject' | 'rueckfrage';

/**
 * POST /agent/confirm request body.
 *
 * `toolCallId` correlates back to the `awaiting_confirmation` SSE event.
 * `note` is required for reject + rueckfrage (enforced server-side, ≥10 chars).
 */
export interface AgentConfirmRequestDto {
  /** Correlation id from the awaiting_confirmation SSE event. */
  toolCallId: string;
  /** The human's explicit decision. */
  decision: AgentDecision;
  /** Mandatory for reject + rueckfrage (≥10 chars). Optional for approve. */
  note?: string;
}

// ── Retrieval seam result ──────────────────────────────────────────────────────

/**
 * A single grounded source returned by the document-retrieval tool.
 * GROUNDING CONTRACT: every field comes from the real retrieval pipeline.
 * The tool NEVER fabricates a source when the corpus is empty or no hit matches.
 */
export interface RetrievalSource {
  /** Document id (provenance handle). */
  docId: string;
  /** Grounded excerpt — NEVER fabricated; the actual text chunk from the document. */
  snippet: string;
  /** Reranker score in [0,1]. */
  score: number;
  /** Provenance label for citation (e.g. document title or doc type). */
  source: string;
}

/**
 * Result of the `search-documents` tool.
 *
 * GROUNDING CONTRACT:
 *   found=false ⟺ sources is empty (corpus is empty OR no match above threshold).
 *   When found=false, the model must say "Dazu habe ich keine Dokumente gefunden."
 *   and NEVER invent a citation.
 */
export interface DocSearchResult {
  /** false ⟺ sources is empty (empty corpus / no hit). */
  found: boolean;
  /** Grounded source excerpts. Empty array when found=false. */
  sources: RetrievalSource[];
}

// ── Tool result payload types ──────────────────────────────────────────────────

/**
 * Result of the `get-pending-work` tool.
 * Lazy summary — counts + a few key items; NOT full lists.
 * Loaded once at chat start as the agent's eager context.
 */
export interface PendingWorkSummary {
  tasks: {
    /** Count of open/in_progress/awaiting_approval tasks. */
    count: number;
    /** Top N (≤5) tasks by created_at DESC. */
    items: Array<{
      id: string;
      title: string;
      status: string;
      process: string;
    }>;
  };
  renewals: {
    /** Count of red (mahnen) signals. */
    red: number;
    /** Count of orange (informieren) signals. */
    orange: number;
    /** Top N (≤5) red signals by renewal_date ASC. */
    items: Array<{
      policy_id: string;
      policy_number: string | null;
      bucket: string;
      renewal_date: string | null;
    }>;
  };
  invoices: {
    /** Count of invoices currently in_review or blocked (awaiting manual attention). */
    awaitingCount: number;
  };
}

/**
 * Result of the `get-entity-summary` tool.
 * Compact summary — never the full DTO.
 * `found: false` when the entity does not exist (the model then says "not found").
 */
export type EntitySummary =
  | { found: false }
  | KundeSummary
  | PoliceSummary;

/** Summary for a Firma/Kunde entity. */
export interface KundeSummary {
  found: true;
  entityType: 'kunde';
  id: string;
  name: string;
  uid: string | null;
  /** e.g. 'open' | 'churned' */
  status: string | null;
  canton: string | null;
}

/** Summary for a Police (policy) entity. */
export interface PoliceSummary {
  found: true;
  entityType: 'police';
  id: string;
  policyNumber: string | null;
  versicherer: string | null;
  sparte: string | null;
  status: string | null;
  renewalDate: string | null;
}

// ── P2b: Invoice write-tool names + generative card facts ─────────────────────

/**
 * The three invoice write tools that share the confirm-before-write protocol
 * and the InvoiceConfirmCard generative UI.
 * [Source: Story P2b spec §2]
 */
export const INVOICE_WRITE_TOOL_NAMES = [
  'approve-invoice',
  'reject-invoice',
  'request-clarification',
] as const;

export type InvoiceWriteToolName = (typeof INVOICE_WRITE_TOOL_NAMES)[number];

/**
 * Typed facts payload for an invoice write-action confirm (cardKind discriminant).
 *
 * `cardKind: 'invoice'` is the discriminant used by `isInvoiceConfirmFacts()` on the web
 * to route to InvoiceConfirmCard vs the generic ConfirmCard.
 *
 * Finance-gated fields (`betrag`, `differenz`) are present ONLY when the user holds
 * `invoice_check.read_amounts`; they are omitted entirely when the permission is absent
 * (InvoiceConfirmCard hides those rows for free).
 *
 * [Source: Story P2b spec §2]
 */
export interface InvoiceConfirmFacts {
  /** Discriminant — routes to InvoiceConfirmCard. */
  cardKind: 'invoice';
  invoiceId: string;
  invoiceNumber: string;
  /** Kundenname (firma.name via policy.subject_id). Falls back to '(unbekannt)'. */
  kundeName: string;
  /** Versicherername (company_master.name via invoice.carrier_uid). Falls back to '(unbekannt)'. */
  versichererName: string;
  carrierUid: string;
  currentStatus: string;
  decision: 'approve' | 'reject' | 'rueckfrage';
  /** Bruttobetrag (amount_gross). Present only with invoice_check.read_amounts. */
  betrag?: string;
  /** Abweichungsbetrag (deviation_absolute from latest invoice_check). Present only with invoice_check.read_amounts. */
  differenz?: string;
}

/**
 * Type guard — narrows `Record<string,unknown>` to `InvoiceConfirmFacts`.
 * Use this on the web side before rendering InvoiceConfirmCard.
 *
 * [Source: Story P2b spec §2]
 */
export function isInvoiceConfirmFacts(f: unknown): f is InvoiceConfirmFacts {
  return (
    typeof f === 'object' &&
    f !== null &&
    (f as Record<string, unknown>)['cardKind'] === 'invoice'
  );
}

// ── P3a: STT transcription response (POST /agent/transcribe) ──────────────────

/**
 * POST /agent/transcribe response (Story P3a). Audio in → text out (input only).
 *
 * The browser POSTs an audio blob (multipart, field "file") and receives `{ text }`.
 * The caller then feeds `text` into the existing POST /agent/chat as a normal message.
 *
 * DESIGN:
 *   - STT is stateless (no DB write, no audit at transcribe time).
 *   - Audit happens downstream when text enters the persisted conversation via /agent/chat.
 *   - RBAC: guarded by `agent.chat` (same permission as chat — a user who can chat can transcribe).
 *   - No TTS: Brok does not speak. This is input only.
 *
 * apps/web imports this type for the `agentTranscribe()` API client (P3b).
 * apps/api imports this type for the controller return type.
 *
 * [Source: Story P3a spec §3]
 */
export interface AgentTranscribeResponseDto {
  /** The transcribed text from the audio input. */
  text: string;
}
